import {
  Box,
  Checkbox,
  Chip,
  ClickAwayListener,
  Fade,
  IconButton,
  InputAdornment,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";
import { FolderSimpleIcon } from "@phosphor-icons/react/dist/csr/FolderSimple";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import React, { Dispatch, Fragment, forwardRef, useEffect, useReducer, useState } from "react";
import { JSX } from "react/jsx-runtime";
import typography from "../../styles/sbDesignSystem/sbTypography";
import { TreeSelectAction, TreeSelectReducer, TreeState } from "./TreeSelectReducer";
const StyledLabel = styled(Box)({
  display: "flex",
  alignItems: "center",
  textTransform: "none",
});
const StyledTreeItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "disabled" && prop !== "isFirstElem",
})<{ disabled?: boolean; isFirstElem?: boolean }>(({ disabled, isFirstElem, theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: isFirstElem ? 0 : "12px",
  borderRadius: "4px",

  ...(!disabled && {
    "&:hover": {
      cursor: "pointer",
      background: theme.palette.surfaceLight30.main,
    },
  }),
}));
const StyledTreeChildren = styled(Box)({
  paddingLeft: "28px",
});
const TreeContainerBox = styled(Box)(({ theme }) => ({
  background: theme.palette.surfaceLight10.main,
  borderRadius: "4px",
  padding: "24px",
  boxShadow: theme.mixins.shadowSurfaceLight10,
  ...theme.applyStyles("dark", {
    boxShadow: theme.palette.surfaceDark10.main,
  }),
  minWidth: "350px",
  maxWidth: "900px",
  minHeight: "150px",
  maxHeight: "60vh",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: theme.palette.surfaceLight30.main,
  overflowY: "scroll",
  transition: "top 300ms linear",
  "::-webkit-scrollbar": {
    width: "5px",
    height: "13px",
  },
  /* Optional: show position indicator in red */
  "::-webkit-scrollbar-thumb": {
    background: theme.palette.surfaceLight50.main,
    borderRadius: "8px",
  },
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.modal + 1, // Needed to display the popper above the modal in downloads page
}));

const TextFieldStyled = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "hasSelectedValue",
})<{
  hasSelectedValue?: boolean;
}>(({ hasSelectedValue }) => {
  return {
    width: "350px",
    // MuiInputBase-formControl is only on the outer OutlinedInput root, not on the nested
    // MUI Input inside CustomInput — so this avoids bleeding styles into the inner Input
    " .MuiInputBase-formControl": {
      padding: hasSelectedValue ? "12px 12px 12px 12px" : undefined,
      marginBottom: "5px",
      height: "50px",
    },
  };
});

const StyledChip = styled(Chip)(({ theme }) => ({
  maxWidth: "150px",
  minWidth: "40px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  padding: "4px 12px 4px 12px",
  borderRadius: "8px",
  background: theme.palette.secondary10.main,
  "& .MuiChip-label": {
    ...(typography && typography["button.xSmall"]),
    paddingLeft: "0",
    paddingRight: "0",
  },
  "& .MuiChip-deleteIcon": {
    marginLeft: "12px",
    marginRight: "0px",
    color: theme.palette.text50.main,
    width: "18px",
    height: "18px",
  },
}));

type TreeItemProps = {
  id: string;
  onSelectCallback: (e: React.MouseEvent<HTMLInputElement>) => void;
  label: string;
  children: ReadonlyArray<JSX.Element>;
  disabled: boolean;
  show: boolean;
  search: string;
  selected: boolean;
  isFirstElem: boolean;
  helperText?: string;
  hide?: boolean;
};

export type RecursiveTreeProps = {
  listMeta: Array<TreeBranch>;
  onSelectCallback: (value: Array<TreeBranch> | TreeBranch) => void;
  selectedValue: TreeBranch[] | string | string[];
  onClear?: () => void;
  multiple?: boolean;
};

type ExtraInfo = {
  filterValue: {
    project?: string | null;
    exportName?: string | null;
    importName?: string | null;
    bundleName?: string | null;
  };
};

type TreeBranch = {
  id: string;
  label: string;
  branches?: Array<TreeBranch>;
  helperText?: string;
  disabled: boolean;
  show: boolean;
  currentValues?: Array<string>;
  extraInfo?: ExtraInfo;
};

const StyledSpan = styled("span", {
  shouldForwardProp: (prop) => prop !== "highlight",
})<{
  highlight?: boolean;
}>(({ highlight = false }) => {
  return {
    ...(highlight && {
      fontWeight: "bold",
    }),
  };
});

const TreeItem = ({
  onSelectCallback,
  label,
  children,
  disabled,
  show,
  search,
  selected,
  isFirstElem,
  helperText,
}: TreeItemProps) => {
  const theme = useTheme();
  const [isOpen, toggleItemOpen] = useState<boolean>(true);
  // https://github.com/moroshko/autosuggest-highlight
  const matches = match(label, search, {
    insideWords: true,
    findAllOccurrences: true,
  });

  const parts = parse(label, matches);
  return (
    <>
      <StyledTreeItem
        className={selected ? "selected" : ""}
        isFirstElem={isFirstElem}
        onClick={
          !disabled
            ? (e: React.MouseEvent<HTMLInputElement>) => {
                onSelectCallback(e);
              }
            : () => null
        }
      >
        <Stack sx={{ width: "100%" }}>
          <Stack direction="row" sx={{ alignItems: "flex-start", gap: "8px", width: "100%" }}>
            <Checkbox checked={selected} disabled={disabled} />
            <StyledLabel>
              <Typography
                data-cy="project-name"
                title={label}
                variant={children.length > 0 ? "body.small.semibold" : "body.medium.regular"}
                sx={{
                  wordBreak: "break-word",
                  ...(disabled && { color: "surfaceLight50.main" }),
                }}
              >
                {parts.map((part, index) => (
                  <StyledSpan key={index} highlight={part.highlight}>
                    {part.text}
                  </StyledSpan>
                ))}
              </Typography>
            </StyledLabel>
          </Stack>
          <Typography
            sx={{
              ml: "32px",
              wordBreak: "break-word",
              color: "text30.main",
            }}
          >
            {helperText}
          </Typography>
        </Stack>
        {children.length > 0 && (
          <Box
            className="icon-container"
            onClick={(e) => {
              e.stopPropagation();
              toggleItemOpen(!isOpen);
            }}
            data-cy="chevron-icon"
            sx={{
              display: "flex",
            }}
          >
            {isOpen || show ? (
              <CaretDownIcon color={theme.palette.text30.main} width="30px" height="30px" />
            ) : (
              <CaretUpIcon color={theme.palette.text30.main} width="30px" height="30px" />
            )}
          </Box>
        )}
      </StyledTreeItem>
      <StyledTreeChildren>{(isOpen || show) && children}</StyledTreeChildren>
    </>
  );
};

const NoData = () => {
  const theme = useTheme();
  return (
    <Stack
      sx={{ alignItems: "center", justifyContent: "center", height: "150px", display: "flex", flexDirection: "column" }}
    >
      <FolderSimpleIcon size="2x" color={theme.palette.text30.main} />
      <Typography sx={{ textTransform: "none", marginTop: "10px" }} data-cy="no-data">
        No Data
      </Typography>
    </Stack>
  );
};

type TreeValue = Array<TreeBranch> | string | string[];

const InputSelectedValues = ({
  currentValues,
  dispatch,
  multiple,
  onSelectCallback,
}: {
  currentValues: TreeValue;
  dispatch: Dispatch<TreeSelectAction>;
  multiple: boolean;
  onSelectCallback: (value: Array<TreeBranch> | TreeBranch) => void;
}) => {
  if (!multiple) return;
  const firstSelected = (
    <StyledChip
      label={(currentValues as TreeBranch[])?.[0]?.label}
      onDelete={() => {
        const newCurrentValues = (currentValues as TreeBranch[]).filter(
          (branch) => branch.label !== (currentValues as TreeBranch[])?.[0]?.label
        );
        dispatch({
          type: "SET_CURRENT_VALUES",
          currentValues: newCurrentValues,
        });
        onSelectCallback(newCurrentValues);
      }}
      deleteIcon={<XIcon />}
    />
  );
  if ((currentValues as TreeBranch[]).length == 1) {
    return <>{firstSelected}</>;
  } else if ((currentValues as TreeBranch[]).length > 1) {
    return (
      <>
        {firstSelected}
        <StyledChip label={`+${(currentValues as TreeBranch[]).length - 1}`} />
      </>
    );
  }
  return null;
};

const CustomInput = forwardRef<
  HTMLInputElement,
  {
    multiple: boolean;
    treeState: TreeState;
    dispatch: Dispatch<TreeSelectAction>;
    anchorEl: HTMLElement;
    onSelectCallback: (value: Array<TreeBranch> | TreeBranch) => void;
  }
>(function CustomInput(props, ref) {
  const { multiple, treeState, dispatch, anchorEl, onSelectCallback, ...rest } = props as typeof props & {
    as?: unknown;
    ownerState?: unknown;
    children?: unknown;
    disableUnderline?: unknown;
  };
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const {
    as: _as,
    ownerState: _ownerState,
    children: _children,
    disableUnderline: _disableUnderline,
    ...inputRest
  } = rest;
  /* eslint-enable @typescript-eslint/no-unused-vars */
  return (
    <>
      {multiple && (
        <Stack direction="row" sx={{ maxWidth: 150, gap: "8px" }}>
          <InputSelectedValues
            currentValues={treeState.currentValues}
            dispatch={dispatch}
            multiple={multiple}
            onSelectCallback={onSelectCallback}
          />
        </Stack>
      )}
      <input
        ref={ref}
        {...(inputRest as React.InputHTMLAttributes<HTMLInputElement>)}
        autoFocus={Boolean(anchorEl)}
        style={{
          paddingLeft: "14px",
          paddingTop: "4px",
          paddingBottom: "5px",
          margin: 0,
          flex: "1",
          font: "inherit",
          color: "inherit",
          border: "none",
          outline: "none",
          background: "none",
          height: "1.4375em",
          minWidth: 0,
          width: "100%",
          boxSizing: "content-box",
        }}
      />
    </>
  );
});

const TreeSelect = ({ listMeta, onSelectCallback, selectedValue, onClear, multiple }: RecursiveTreeProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [search, setSearch] = useState<string>("");
  const [treeState, dispatch] = useReducer(
    TreeSelectReducer,
    { listMeta: listMeta, currentValues: selectedValue },
    (initial) => TreeSelectReducer(initial, { type: "SEARCH_PROJECT", name: "" })
  );

  useEffect(() => {
    // If the selected value is updated from the parent component we need to update the currentValues
    dispatch({ type: "SET_CURRENT_VALUES", currentValues: selectedValue });
  }, [selectedValue]);
  const handleClickClear = () => {
    setSearch("");
    onClear?.();
    if (multiple) {
      dispatch({ type: "SET_CURRENT_VALUES", currentValues: [] });
    } else {
      dispatch({ type: "SET_CURRENT_VALUES", currentValues: "" });
    }
    dispatch({ type: "SEARCH_PROJECT", name: "" });
  };
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    dispatch({ type: "SEARCH_PROJECT", name: event.target.value });
  };

  const hasSearchResults = () => {
    return treeState.listMeta.filter((elem) => elem.show).length > 0;
  };

  const createTree = (branch: TreeBranch, isFirstElem: boolean) =>
    branch.branches && branch.show ? (
      <TreeItem
        isFirstElem={isFirstElem}
        id={branch.id}
        key={branch.label}
        onSelectCallback={() => {
          if (!multiple) {
            setAnchorEl(null);
            onSelectCallback(branch);
            setSearch(branch.label);
            dispatch({
              type: "SET_CURRENT_VALUES",
              currentValues: branch.label,
            });
            return;
          }
          const currentValuesAux = treeState.currentValues as Array<TreeBranch>;
          if (currentValuesAux.filter((elem) => elem.label === branch.label).length > 0) {
            if (branch?.extraInfo?.filterValue.project) {
              // Remove the parent element
              let newCurrentValues = currentValuesAux.filter((elem) => elem.label !== branch.label);
              // remove all the childrens
              branch.branches?.forEach((child) => {
                newCurrentValues = newCurrentValues.filter((elem) => elem.label !== child.label);
              });

              dispatch({
                type: "SET_CURRENT_VALUES",
                currentValues: newCurrentValues,
              });
            } else {
              dispatch({
                type: "SET_CURRENT_VALUES",
                currentValues: currentValuesAux.filter((elem) => elem.label !== branch.label),
              });
            }
          } else {
            if (branch.extraInfo?.filterValue.project) {
              const newCurrentValues = [...currentValuesAux, branch];
              branch.branches?.forEach((child) => {
                if (!newCurrentValues.includes(child)) {
                  newCurrentValues.push(child);
                }
              });
              dispatch({
                type: "SET_CURRENT_VALUES",
                currentValues: newCurrentValues,
              });
            } else {
              dispatch({
                type: "SET_CURRENT_VALUES",
                currentValues: [...currentValuesAux, branch],
              });
            }
          }
        }}
        label={branch.label}
        disabled={branch.disabled}
        helperText={branch.helperText}
        show={search !== ""}
        search={search}
        selected={
          multiple
            ? (treeState.currentValues as TreeBranch[]).filter((elem) => elem.label === branch.label).length > 0
            : (treeState.currentValues as string) === branch.label
        }
      >
        {branch.branches.map((branch: TreeBranch) => {
          return <Fragment key={branch.label}>{createTree(branch, false)}</Fragment>;
        })}
      </TreeItem>
    ) : null;
  return (
    <ClickAwayListener
      onClickAway={() => {
        if (anchorEl && multiple) {
          onSelectCallback(treeState.currentValues as TreeBranch[]);
        }
        setAnchorEl(null);
      }}
    >
      <div>
        <TextFieldStyled
          data-cy="select-textbox"
          placeholder={"Please Select"}
          hasSelectedValue={multiple ? (treeState.currentValues as TreeBranch[]).length > 0 : false}
          slots={{ htmlInput: CustomInput }}
          slotProps={{
            htmlInput: {
              disableUnderline: true,
              multiple: multiple,
              treeState: treeState,
              dispatch: dispatch,
              anchorEl: anchorEl,
              onSelectCallback: onSelectCallback,
            } as React.ComponentProps<typeof CustomInput>,
            input: {
              endAdornment: (
                <>
                  {onClear ? (
                    <InputAdornment position="end">
                      <Tooltip id="tooltip" placement="right" arrow title="clear">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClickClear();
                          }}
                          data-cy="clear-treeselect"
                        >
                          <XIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null}
                </>
              ),
              style: {
                paddingRight: 0,
              },
            },
          }}
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
          onChange={handleTextChange}
          value={search}
          focused={false}
          autoComplete="off"
        />
        <StyledPopper open={Boolean(anchorEl)} anchorEl={anchorEl} transition>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <TreeContainerBox data-cy="tree-container">
                {treeState.listMeta.length === 0 || !hasSearchResults() ? (
                  <NoData />
                ) : (
                  <>
                    {treeState.listMeta.map((branch: TreeBranch, idx) => (
                      <Fragment key={branch.id}>{createTree(branch, idx == 0)}</Fragment>
                    ))}
                  </>
                )}
              </TreeContainerBox>
            </Fade>
          )}
        </StyledPopper>
      </div>
    </ClickAwayListener>
  );
};

export { TreeBranch, TreeSelect };
export type { TreeValue };
